import { Location } from "@angular/common";
import { Component, ElementRef, Input, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { BaseComponent, UserService, ProductService, AdminService, WindowService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { BytesToSizePipe } from "libs/xplat/features/src/lib/ui/pipes/bytestosize.pipe";

import { saveAs } from "file-saver";
import { forkJoin, interval, Subject } from "rxjs";
import { map, skip, takeUntil, throttle } from "rxjs/operators";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-admin-subscription-product-list",
  templateUrl: "subscription-product-list.component.html",
  standalone: false,
})
export class AdminSubscriptionProductListComponent extends BaseComponent implements OnInit {
  public loading: boolean;
  public subscriptionProducts: any = {};
  public sections: any = [
    {
      name: "Software",
      id: "software",
      selected: true,
    },
    {
      name: "Extensions",
      id: "extensions",
    },
  ];
  public selectedSection = this.sections[0].id;
  public rollupNotice =
    "Note: Files that ship per platform (Windows/macOS/Linux) are rolled up into a single file, sized by the largest platform version. Actual per-platform file counts may be higher.";

  private _associatedProductsQuery: string;
  private _associatedProductsQueryStream$: Subject<any> = new Subject();
  set associatedProductsQuery(query) {
    this._associatedProductsQuery = query;
    if (this._associatedProductsQuery) {
      this._associatedProductsQueryStream$.next(query);
    } else {
      this.associatedProductResults = undefined;
    }
  }
  get associatedProductsQuery() {
    return this._associatedProductsQuery;
  }
  public associatedProductResults: any;

  constructor(
    userService: UserService,
    public productService: ProductService,
    public adminService: AdminService,
    public appService: AppService,
    private _activatedRoute: ActivatedRoute,
    public router: Router,
    public location: Location,
    private _win: WindowService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.adminService.get(`subscription/group/1`).subscribe((result: any) => {
      this.subscriptionProducts = result;
    });
    this._associatedProductsQueryStream$
      .pipe(
        skip(1),
        throttle(
          () => {
            return interval(1000);
          },
          { leading: true, trailing: true }
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.productAutocomplete();
      });
  }

  productAutocomplete() {
    if (this.associatedProductsQuery) {
      this.productService.searchv2("title", this.associatedProductsQuery, 10).subscribe((result: any) => {
        this.associatedProductResults = result;
        this.associatedProductResults.forEach((prod) => {
          prod.added = this.subscriptionProducts.software.filter((product) => product.id == prod.id).length > 0;
          prod.added = prod.added || this.subscriptionProducts.extensions.filter((product) => product.id == prod.id).length > 0;
        });
      });
    } else {
      this.associatedProductResults = [];
    }
  }

  setAssociatedProd(prod) {
    let key = prod.DTYPE == "SoftwarePackage" ? "software" : "extensions";
    if (this.subscriptionProducts[key].filter((product) => product.id == prod.id).length == 0) {
      this.subscriptionProducts[key].push({
        title: prod.title,
        imageUrl: prod.imageUrl,
        id: prod.id,
        stringId: prod.stringId,
        shortDescription: prod.shortDescription,
        description: prod.description,
      });
    }
    this.subscriptionProducts[key].sort((a, b) => a.title.localeCompare(b.title));
    prod.added = true;
    this.adminService
      .put(`subscription/group/1/products`, {
        type: this.selectedSection,
        productId: prod.id,
        action: "add",
      })
      .subscribe();
  }

  selectSection(id) {
    this.selectedSection = id;
    this.sections.forEach((section) => {
      section.selected = false;
      if (section.id == id) section.selected = true;
    });
  }

  goToProduct(prod) {
    this.router.navigate(["products/detail", prod.id]);
  }

  removeProduct(prod, event?) {
    // Prevent the surrounding product link from navigating when the trash icon is clicked.
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.appService
      .confirm("You are removing a product from Subscription. This takes effect immediately and all current subscribers will see this change.", "Remove product", "Cancel")
      .then((confirmed) => {
        if (!confirmed) return;
        this.subscriptionProducts[this.selectedSection] = this.subscriptionProducts[this.selectedSection].filter((product) => product.id != prod.id);
        this.adminService
          .put(`subscription/group/1/products`, {
            productId: prod.id,
            action: "remove",
          })
          .subscribe();
      });
  }

  totalFileSize(section: "software" | "extensions"): number {
    const products = this.subscriptionProducts[section];
    if (!products) return 0;
    return products.reduce((sum, product) => sum + (product.totalFileSize || 0), 0);
  }

  get softwareTotalFileSize(): number {
    return this.totalFileSize("software");
  }

  get extensionsTotalFileSize(): number {
    return this.totalFileSize("extensions");
  }

  // Escapes a single CSV cell value.
  private _csvCell(value: any): string {
    const str = value == null ? "" : String(value);
    return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  }

  // Builds a CSV: a title line, the roll-up notice, a blank row, the header, then the rows.
  private _buildCsv(title: string, headers: string[], rows: any[][]): string {
    const lines = [this._csvCell(title), this._csvCell(this.rollupNotice), ""];
    lines.push(headers.map((cell) => this._csvCell(cell)).join(","));
    rows.forEach((row) => lines.push(row.map((cell) => this._csvCell(cell)).join(",")));
    return lines.join("\r\n");
  }

  // DENG-561: export the included products as two CSVs — a per-product summary and a
  // per-file detail listing. Both cover software and extensions and carry the roll-up notice.
  downloadList() {
    const bytesToSize = new BytesToSizePipe();
    const products = [
      ...(this.subscriptionProducts.software || []).map((product) => ({ product, type: "Software" })),
      ...(this.subscriptionProducts.extensions || []).map((product) => ({ product, type: "Extension" })),
    ];

    const summaryRows: any[][] = products.map(({ product, type }) => [product.title, type, product.fileCount || 0, product.totalFileSize || 0, bytesToSize.transform(product.totalFileSize || 0)]);
    const grandTotal = products.reduce((sum, { product }) => sum + (product.totalFileSize || 0), 0);
    summaryRows.push([`TOTAL (${products.length} products)`, "", "", grandTotal, bytesToSize.transform(grandTotal)]);
    const summaryCsv = this._buildCsv("FSP+ Included Products — Summary", ["Product Title", "Type", "File Count", "Total File Size (bytes)", "Total File Size"], summaryRows);
    saveAs(new Blob([summaryCsv], { type: "text/csv;charset=utf-8;" }), "fsp-included-products-summary.csv");

    const detailRows: any[][] = [];
    products.forEach(({ product, type }) => {
      (product.files || []).forEach((file) => {
        detailRows.push([product.title, type, file.fileName, file.contentLength || 0, bytesToSize.transform(file.contentLength || 0)]);
      });
    });
    const detailCsv = this._buildCsv("FSP+ Included Products — Files", ["Product Title", "Type", "File Name", "File Size (bytes)", "File Size"], detailRows);
    saveAs(new Blob([detailCsv], { type: "text/csv;charset=utf-8;" }), "fsp-included-products-files.csv");
  }
}
